/* eslint-disable no-unused-vars */
/* eslint-disable no-eval */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  addMonths, parseISO, differenceInDays,
} from 'date-fns';
import {
  expiredAccountColumns, riskAccountsColumns, decliningAccountColumns, trendAccountColumns,
} from '../../utils/dataGridColumns';
import { getDate, isAfter90Days } from '../../utils/getDate';
import {
  AccountNameCell, AccountSignalCell, HealthScore, HealthScoreTrendThumbnail, RenewalDateCell, SeatsCell,
} from '../@dashboard/account-details/accountDetailsTable';

function isWithinDateRange(date, startDate, endDate) {
  if (!startDate || !endDate) {
    return true;
  }
  const currentDate = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);
  return currentDate >= start && currentDate <= end;
}

const healthScoreRange = [0, 30];
const expiredInMonths = 3;
const currentDate = new Date();
const threeMonthsLater = addMonths(currentDate, expiredInMonths);

export const useDashboardHealthScore = () => {
  const [riskRows, setRiskRows] = useState([]);
  const [expiredRows, setExpiredRows] = useState([]);
  const [decliningRows, setDecliningRows] = useState([]);
  const [trendRows, setTrendRows] = useState([]);
  const [decliningAccountsList, setDecliningAccountsList] = useState([]);
  const [trendAccountsList, setTrendAccountsList] = useState([]);
  const [riskAccountList, setRiskAccountList] = useState([]);
  const [expiredAccountsList, setExpiredAccountsList] = useState([]);
  const [loadingState, setLoadingState] = useState(false);

  const allAccounts = useSelector((state) => state.accountsWithLicenseList);
  const siteConfiguration = useSelector((state) => state.siteConfiguration.data);

  const handleSortTrendValueAccounts = (array, field, type = 'desc') => {
    const modifiedRiskAccount = array
      ?.sort((a, b) => {
        if (a.packageEnabled !== b.packageEnabled) {
          return a.packageEnabled ? -1 : 1;
        }
        if (type === 'asc' && a[field]) {
          return (a[field] - b[field]);
        }
        return (b[field] - a[field]);
      });
    return modifiedRiskAccount;
  };

  const handleScoreAccounts = (array) => {
    const modifiedScoreAccount = array
      ?.sort((a, b) => {
        if (a.packageEnabled !== b.packageEnabled) {
          return a.packageEnabled ? -1 : 1;
        }
        return a.healthScore - b.healthScore;
      });
    return modifiedScoreAccount;
  };

  const handleExpiredAccount = (array) => {
    const modifiedExpiredData = array
      ?.filter((item) => {
        const isRenewalDateRangeMatch = isWithinDateRange(
          item.renewalDate,
          currentDate,
          threeMonthsLater,
        );
        return isRenewalDateRangeMatch;
      }).sort((a, b) => {
        if (a.packageEnabled !== b.packageEnabled) {
          return a.packageEnabled ? -1 : 1;
        }
        const dateA = parseISO(a.renewalDate);
        const dateB = parseISO(b.renewalDate);
        return differenceInDays(dateA, dateB);
      });
    return modifiedExpiredData;
  };

  useEffect(() => {
    const modifiedRiskAccounts = riskAccountList ? handleScoreAccounts(riskAccountList) : [];
    setRiskRows(modifiedRiskAccounts);
    setLoadingState(false);
  }, [riskAccountList]);

  useEffect(() => {
    const modifiedExpiredAccount = expiredAccountsList ? handleExpiredAccount(expiredAccountsList) : [];
    setExpiredRows(modifiedExpiredAccount);
    setLoadingState(false);
  }, [expiredAccountsList]);

  useEffect(() => {
    const modifiedDecliningAccounts = decliningAccountsList ? handleSortTrendValueAccounts(decliningAccountsList, (siteConfiguration?.scoreTrendTimeframe === 'weekly' ? 'weeklyTrendValue' : 'trendValue'), 'asc') : [];
    setDecliningRows(modifiedDecliningAccounts);
    setLoadingState(false);
  }, [decliningAccountsList]);

  useEffect(() => {
    const modifiedTrendAccounts = trendAccountsList ? handleSortTrendValueAccounts(trendAccountsList, (siteConfiguration?.scoreTrendTimeframe === 'weekly' ? 'weeklyTrendValue' : 'trendValue')) : [];
    setTrendRows(modifiedTrendAccounts);
    setLoadingState(false);
  }, [trendAccountsList]);

  const isPackageDisabled = (params) => {
    const { packageEnabled, isEnabled } = params;
    if (packageEnabled === false || isEnabled === false) return true;
    return false;
  };

  const riskAccountsColumnInfo = riskAccountsColumns((siteConfiguration?.scoreTrendTimeframe === 'weekly') ? 'weeklyTrendValue' : 'trendValue');
  riskAccountsColumnInfo.forEach((item) => {
    if (item.field === 'healthScore') {
      item.renderCell = (params) => <HealthScore params={params} />;
    } else if (item.field === 'seatsAssigned') {
      item.renderCell = (params) => <SeatsCell params={params} />;
    } else if (item.field === 'accountName') {
      item.renderCell = (params) => <AccountNameCell params={params} />;
    } else if (item.field === 'healthScoreTrendThumbnail') {
      item.renderCell = (params) => <HealthScoreTrendThumbnail params={params} view={siteConfiguration?.scoreTrendTimeframe ?? 'monthly'} trendsData={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendsData || []) : (params?.row?.trendsData || [])} />;
    } else if (item.field === 'trendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    } else if (item.field === 'weeklyTrendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    }
    return false;
  });

  const decliningAccountColumnInfo = decliningAccountColumns((siteConfiguration?.scoreTrendTimeframe === 'weekly') ? 'weeklyTrendValue' : 'trendValue');
  decliningAccountColumnInfo.forEach((item) => {
    if (item.field === 'healthScore') {
      item.renderCell = (params) => <HealthScore params={params} />;
    } else if (item.field === 'seatsAssigned') {
      item.renderCell = (params) => <SeatsCell params={params} />;
    } else if (item.field === 'accountName') {
      item.renderCell = (params) => <AccountNameCell params={params} />;
    } else if (item.field === 'healthScoreTrendThumbnail') {
      item.renderCell = (params) => <HealthScoreTrendThumbnail params={params} view={siteConfiguration?.scoreTrendTimeframe ?? 'monthly'} trendsData={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendsData || []) : (params?.row?.trendsData || [])} />;
    } else if (item.field === 'trendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    } else if (item.field === 'weeklyTrendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    }
    return false;
  });

  const expiredAccountColumnInfo = expiredAccountColumns((siteConfiguration?.scoreTrendTimeframe === 'weekly') ? 'weeklyTrendValue' : 'trendValue');
  expiredAccountColumnInfo.forEach((item) => {
    if (item.field === 'healthScore') {
      item.renderCell = (params) => <HealthScore params={params} />;
    } else if (item.field === 'renewalDate') {
      item.renderCell = (params) => <RenewalDateCell params={params} />;
    } else if (item.field === 'accountName') {
      item.renderCell = (params) => <AccountNameCell params={params} />;
    } else if (item.field === 'healthScoreTrendThumbnail') {
      item.renderCell = (params) => <HealthScoreTrendThumbnail params={params} view={siteConfiguration?.scoreTrendTimeframe ?? 'monthly'} trendsData={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendsData || []) : (params?.row?.trendsData || [])} />;
    } else if (item.field === 'trendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    } else if (item.field === 'weeklyTrendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    }
    return false;
  });

  const trendAccountColumnInfo = trendAccountColumns((siteConfiguration?.scoreTrendTimeframe === 'weekly') ? 'weeklyTrendValue' : 'trendValue');
  trendAccountColumnInfo.forEach((item) => {
    if (item.field === 'healthScore') {
      item.renderCell = (params) => <HealthScore params={params} />;
    } else if (item.field === 'seatsAssigned') {
      item.renderCell = (params) => <SeatsCell params={params} />;
    } else if (item.field === 'accountName') {
      item.renderCell = (params) => <AccountNameCell params={params} />;
    } else if (item.field === 'healthScoreTrendThumbnail') {
      item.renderCell = (params) => <HealthScoreTrendThumbnail params={params} view={siteConfiguration?.scoreTrendTimeframe ?? 'monthly'} trendsData={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendsData || []) : (params?.row?.trendsData || [])} />;
    } else if (item.field === 'trendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    } else if (item.field === 'weeklyTrendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    }
    return false;
  });

  const filterDataByCondition = (array, condition = 'healthScore <= 30') => {
    return array.filter(({
      healthScore, healthScoreTrend, assignedSeatsPercent, weeklyTrend, installDate, visibility, renewalDate, status,
    }) => {
      let updateCondition = condition;
      const trend = siteConfiguration?.scoreTrendTimeframe === 'weekly';
      if (trend) {
        updateCondition = condition.replace(/healthScoreTrend/g, 'weeklyTrend');
      }
      return !(installDate && isAfter90Days(installDate)) && ((renewalDate && getDate(renewalDate) >= currentDate && status === 'Active') || (renewalDate === null && status === 'Active')) && eval(updateCondition);
    });
  };

  useEffect(() => {
    if (allAccounts?.data?.length > 0) {
      const filteredAccountList = filterDataByCondition(allAccounts.data, siteConfiguration?.highRiskAccountConfig?.condition);
      setRiskAccountList(filteredAccountList);
      const expiredAccountList = allAccounts.data.filter((item) => (((item.renewalDate && getDate(item.renewalDate) >= currentDate && item.status === 'Active') || (item.renewalDate === null && item.status === 'Active'))));
      setExpiredAccountsList(expiredAccountList);
      const decliningAccountList = allAccounts.data.filter((item) => (((siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (item.weeklyTrend && item.weeklyTrend === 'falling') : (item.healthScoreTrend && item.healthScoreTrend === 'falling')) && ((item.renewalDate && getDate(item.renewalDate) >= currentDate && item.status === 'Active') || (item.renewalDate === null && item.status === 'Active')))));
      setDecliningAccountsList(decliningAccountList);
      const trendAccountList = allAccounts.data.filter((item) => (((siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (item.weeklyTrend && item.weeklyTrend === 'rising') : (item.healthScoreTrend && item.healthScoreTrend === 'rising')) && ((item.renewalDate && getDate(item.renewalDate) >= currentDate && item.status === 'Active') || (item.renewalDate === null && item.status === 'Active')))));
      setTrendAccountsList(trendAccountList);
    } else {
      setRiskAccountList([]);
      setExpiredAccountsList([]);
      setDecliningAccountsList([]);
      setTrendAccountsList([]);
    }
  }, [allAccounts?.data, siteConfiguration]);

  const accountNum = riskRows?.length;
  const expiredAccountNum = expiredRows?.length;
  const decliningNum = decliningAccountsList?.length;
  const trendAccountNum = trendAccountsList?.length;

  return {
    riskAccountList,
    riskRows,
    expiredRows,
    accountNum,
    healthScoreRange,
    loading: allAccounts?.loading,
    error: allAccounts?.error,
    riskAccountsColumnInfo,
    expiredAccountColumnInfo,
    expiredAccountNum,
    expiredInMonths,
    threeMonthsLater,
    currentDate,
    decliningAccountColumnInfo,
    trendAccountColumnInfo,
    decliningRows,
    trendRows,
    decliningNum,
    trendAccountNum,
    loadingState,
    setLoadingState,
  };
};
